import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, RefreshCw, MessageSquare, X, Check, Brain, Lightbulb } from 'lucide-react';

// Simple markdown renderer for chat messages
const renderMarkdown = (text) => {
    if (!text) return null;

    const paragraphs = text.split(/\n\n+/);

    return paragraphs.map((paragraph, pIdx) => {
        // Check for bullet lists
        if (paragraph.trim().startsWith('- ') || paragraph.trim().startsWith('* ')) {
            const items = paragraph.split('\n').filter(line => line.trim());
            return (
                <ul key={pIdx} style={{ margin: '0.5em 0', paddingLeft: '1.25em' }}>
                    {items.map((item, idx) => {
                        const text = item.replace(/^[-*]\s*/, '');
                        return <li key={idx} style={{ marginBottom: '0.25em' }}>{processInlineFormatting(text)}</li>;
                    })}
                </ul>
            );
        }

        // Check for numbered lists
        if (/^\d+\.\s/.test(paragraph.trim())) {
            const items = paragraph.split('\n').filter(line => line.trim());
            return (
                <ol key={pIdx} style={{ margin: '0.5em 0', paddingLeft: '1.25em' }}>
                    {items.map((item, idx) => {
                        const text = item.replace(/^\d+\.\s*/, '');
                        return <li key={idx} style={{ marginBottom: '0.25em' }}>{processInlineFormatting(text)}</li>;
                    })}
                </ol>
            );
        }

        const lines = paragraph.split('\n');
        const processedLines = lines.map((line, lIdx) => (
            <span key={lIdx}>
                {processInlineFormatting(line)}
                {lIdx < lines.length - 1 && <br />}
            </span>
        ));

        return (
            <p key={pIdx} style={{ margin: pIdx > 0 ? '0.75em 0 0 0' : '0' }}>
                {processedLines}
            </p>
        );
    });
};

// Process bold, italic, and inline code
const processInlineFormatting = (text) => {
    if (!text) return null;

    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
        const boldMatch = remaining.match(/^(.*?)(\*\*|__)(.+?)\2(.*)$/s);
        if (boldMatch) {
            if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
            parts.push(<strong key={key++}>{boldMatch[3]}</strong>);
            remaining = boldMatch[4];
            continue;
        }

        const italicMatch = remaining.match(/^(.*?)(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)(.*)$/s);
        if (italicMatch) {
            if (italicMatch[1]) parts.push(<span key={key++}>{italicMatch[1]}</span>);
            parts.push(<em key={key++}>{italicMatch[2]}</em>);
            remaining = italicMatch[3];
            continue;
        }

        const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/s);
        if (codeMatch) {
            if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
            parts.push(
                <code key={key++} style={{
                    background: 'var(--color-bg-tertiary)',
                    padding: '0.1em 0.3em',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.9em'
                }}>
                    {codeMatch[2]}
                </code>
            );
            remaining = codeMatch[3];
            continue;
        }

        parts.push(<span key={key++}>{remaining}</span>);
        break;
    }

    return parts.length > 0 ? parts : text;
};

/**
 * PromptBuilderChat Component
 * A conversational interface to help users craft refinement prompts.
 */
const PromptBuilderChat = ({
    originalPrompt,
    originalImageUrl,
    onApplyPrompt,
    onClose,
    userId,
    brandId,
    referenceImages = [],
    context = {}
}) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentBuildPrompt, setCurrentBuildPrompt] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const hasInitialized = useRef(false);

    // Initialize with an automatic image scan
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;

            const performInitialScan = async () => {
                setIsLoading(true);
                try {
                    const response = await fetch('http://localhost:3001/api/build-prompt', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            userId,
                            brandId,
                            originalPrompt,
                            originalImageUrl,
                            referenceImages,
                            currentInput: "Analyze the source image, the reference style images (if any), and the original prompt. As my Art Director, determine how the styles from the references could be applied to the source image and suggest a refinement prompt that merges them.",
                            conversationHistory: [],
                            context
                        })
                    });

                    if (!response.ok) throw new Error('Failed to scan image');

                    const data = await response.json();
                    console.log('AI Raw Response:', data.response);
                    const extractedPrompt = extractPrompt(data.response);
                    const cleanedContent = cleanText(data.response);

                    if (extractedPrompt) {
                        setCurrentBuildPrompt(extractedPrompt);
                    }

                    const welcomeMessage = {
                        id: Date.now(),
                        role: 'assistant',
                        content: cleanedContent,
                        timestamp: new Date()
                    };
                    setMessages([welcomeMessage]);
                } catch (error) {
                    console.error('Initial scan error:', error);
                    const fallbackMessage = {
                        id: Date.now(),
                        role: 'assistant',
                        content: "Hi! I'm your AI Art Director. I'm ready to help you refine this image. What specifics would you like to change or improve?",
                        timestamp: new Date()
                    };
                    setMessages([fallbackMessage]);
                } finally {
                    setIsLoading(false);
                }
            };

            performInitialScan();
        }
    }, [userId, brandId, originalPrompt, originalImageUrl, referenceImages, context]);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const extractPrompt = (text) => {
        if (!text) return null;
        // Match content inside tags, OR from an open tag to the end of the string
        const match = text.match(/<current_prompt>([\s\S]*?)(?:<\/current_prompt>|$)/);
        return match ? match[1].trim() : null;
    };

    const cleanText = (text) => {
        if (!text) return "";
        // Replace COMPLETE tags (remove both tag and the content inside)
        let cleaned = text.replace(/<current_prompt>[\s\S]*?<\/current_prompt>/g, '');

        // For unclosed tags (e.g. if the AI was truncated), 
        // just remove the tag marker itself but KEEP the content so the user can see it
        cleaned = cleaned.replace(/<current_prompt>/g, '');

        return cleaned.trim();
    };

    const handleSendMessage = async (messageText = inputValue) => {
        if (!messageText.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: messageText.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
        }
        setIsLoading(true);

        try {
            const response = await fetch('http://localhost:3001/api/build-prompt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    brandId,
                    originalPrompt,
                    originalImageUrl,
                    referenceImages,
                    currentInput: messageText.trim(),
                    conversationHistory: messages.map(m => ({ role: m.role, content: m.content })),
                    context
                })
            });

            if (!response.ok) throw new Error('Failed to get guidance');

            const data = await response.json();
            console.log('AI Raw Response:', data.response);
            const extractedPrompt = extractPrompt(data.response);
            const cleanedContent = cleanText(data.response);

            if (extractedPrompt) {
                setCurrentBuildPrompt(extractedPrompt);
            }

            const assistantMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: cleanedContent,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error in prompt builder:', error);
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                role: 'assistant',
                content: "I'm having a bit of trouble right now. Please try again!",
                isError: true
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'var(--color-bg-primary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            overflow: 'hidden',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            animation: 'fadeIn 0.2s ease-out'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--color-bg-secondary)',
                borderBottom: '1px solid var(--color-border-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Brain size={18} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>AI Prompt Director</span>
                </div>
                <button
                    onClick={onClose}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)' }}
                >
                    <X size={18} />
                </button>
            </div>

            {/* Messages */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-4)',
                scrollBehavior: 'smooth'
            }}>
                {messages.map(m => (
                    <div key={m.id} style={{
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                        padding: 'var(--space-3) var(--space-4)',
                        borderRadius: 'var(--radius-lg)',
                        background: m.role === 'user'
                            ? 'linear-gradient(135deg, var(--color-primary) 0%, #4f46e5 100%)'
                            : 'var(--color-bg-tertiary)',
                        color: m.role === 'user' ? 'white' : 'var(--color-text-primary)',
                        fontSize: 'var(--font-size-sm)',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        border: m.role === 'user' ? 'none' : '1px solid var(--color-border)',
                        boxShadow: m.role === 'user' ? '0 4px 12px rgba(99, 102, 241, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                        borderLeft: m.role === 'assistant' ? '3px solid var(--color-primary)' : undefined,
                        position: 'relative'
                    }}>
                        {m.role === 'assistant' ? renderMarkdown(m.content) : m.content}
                    </div>
                ))}
                {isLoading && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        color: 'var(--color-text-tertiary)',
                        padding: 'var(--space-2)'
                    }}>
                        <div className="dot-flashing"></div>
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500 }}>AI Director is thinking...</span>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Current Draft / Apply Area */}
            {currentBuildPrompt && (
                <div style={{
                    padding: 'var(--space-4)',
                    background: 'linear-gradient(180deg, var(--color-bg-secondary) 0%, var(--color-bg-tertiary) 100%)',
                    borderTop: '2px solid var(--color-primary)',
                    boxShadow: '0 -4px 15px rgba(0,0,0,0.1)',
                    zIndex: 5
                }}>
                    <div style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-primary)',
                        marginBottom: 'var(--space-2)',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}>
                        <Sparkles size={14} />
                        Optimized Prompt Recommendation
                    </div>
                    <div style={{
                        fontSize: 'var(--font-size-sm)',
                        fontStyle: 'italic',
                        color: 'var(--color-text-primary)',
                        marginBottom: 'var(--space-3)',
                        background: 'rgba(255,255,255,0.05)',
                        padding: 'var(--space-2) var(--space-3)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px dashed var(--color-border)',
                        lineHeight: 1.4,
                        maxHeight: '120px',
                        overflowY: 'auto'
                    }}>
                        "{currentBuildPrompt}"
                    </div>
                    <button
                        onClick={() => onApplyPrompt(currentBuildPrompt)}
                        className="btn btn-primary"
                        style={{
                            width: '100%',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '12px',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                        }}
                    >
                        <Check size={18} />
                        Apply Changes
                    </button>
                </div>
            )}

            {/* Input */}
            <div style={{
                padding: 'var(--space-3)',
                background: 'var(--color-bg-secondary)',
                display: 'flex',
                gap: 'var(--space-2)'
            }}>
                <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        // Auto-resize textarea
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                    placeholder="Type your idea... (Enter to send, Shift+Enter for new line)"
                    rows={1}
                    style={{
                        flex: 1,
                        padding: 'var(--space-2) var(--space-3)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--color-border)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                        outline: 'none',
                        resize: 'none',
                        minHeight: '38px',
                        maxHeight: '150px',
                        lineHeight: '1.5',
                        overflowY: 'auto'
                    }}
                    disabled={isLoading}
                />
                <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isLoading}
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: 'none',
                        background: 'var(--color-primary)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        opacity: (!inputValue.trim() || isLoading) ? 0.5 : 1
                    }}
                >
                    <Send size={18} />
                </button>
            </div>

            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .dot-flashing {
          position: relative;
          width: 6px;
          height: 6px;
          border-radius: 5px;
          background-color: var(--color-primary);
          color: var(--color-primary);
          animation: dot-flashing 1s infinite linear alternate;
          animation-delay: 0.5s;
          margin-left: 10px;
          margin-right: 15px;
        }
        .dot-flashing::before, .dot-flashing::after {
          content: "";
          display: inline-block;
          position: absolute;
          top: 0;
        }
        .dot-flashing::before {
          left: -12px;
          width: 6px;
          height: 6px;
          border-radius: 5px;
          background-color: var(--color-primary);
          color: var(--color-primary);
          animation: dot-flashing 1s infinite linear alternate;
          animation-delay: 0s;
        }
        .dot-flashing::after {
          left: 12px;
          width: 6px;
          height: 6px;
          border-radius: 5px;
          background-color: var(--color-primary);
          color: var(--color-primary);
          animation: dot-flashing 1s infinite linear alternate;
          animation-delay: 1s;
        }
        @keyframes dot-flashing {
          0% { background-color: var(--color-primary); box-shadow: 0 0 0 white; }
          50%, 100% { background-color: var(--color-bg-tertiary); }
        }
      `}</style>
        </div>
    );
};

export default PromptBuilderChat;
